import React from 'react';
import { FileText, Download, Image as ImageIcon, Film, Music } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { getConfig } from '../../utils/config';

// Resolve relative attachment URLs against the API backend origin
const API_BASE = (() => {
    const url = getConfig('API_URL') || '';
    return url.replace(/\/api\/v1\/?$/, '');
})();

function resolveUrl(url) {
    if (!url) return url;
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
}

export default function TeamChatFilePreview({ attachments }) {
    const { theme } = useTheme();

    if (!attachments || attachments.length === 0) return null;

    const getIcon = (type) => {
        if (type?.startsWith('image/')) return <ImageIcon size={16} />;
        if (type?.startsWith('video/')) return <Film size={16} />;
        if (type?.startsWith('audio/')) return <Music size={16} />;
        return <FileText size={16} />;
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map((file, index) => {
                const fileUrl = resolveUrl(file.url);

                if (file.type?.startsWith('image/')) {
                    return (
                        <a
                            key={index}
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block max-w-full"
                        >
                            <img
                                src={fileUrl}
                                alt={file.name}
                                className="rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            />
                        </a>
                    );
                }

                return (
                    <a
                        key={index}
                        href={fileUrl}
                        download={file.name}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border max-w-full ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-650'
                                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                        } transition-colors`}
                    >
                        <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                            {getIcon(file.type)}
                        </span>
                        <div className="min-w-0">
                            <p className={`text-sm font-rasa truncate max-w-[200px] ${
                                theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                            }`}>{file.name}</p>
                            <p className={`text-xs font-rasa ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>{formatSize(file.size)}</p>
                        </div>
                        <Download size={14} className={`flex-shrink-0 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                    </a>
                );
            })}
        </div>
    );
}
